import { Box, Divider, Typography } from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { ApiError } from 'src/types/interfaces/api-error';

import { Dialog, DialogActions } from '../../../../../components/molecules/dialog';

import { ListCard } from '@/components/atoms/list-card';
import { Query } from '@/components/atoms/query';
import { DataStatusIndicator } from '@/components/molecules/data-status-indicator/data-status-indicator';
import { CompactPagination } from '@/components/organisms/compact-pagination';
import { destinationsKeys } from '@/features/destinations/api/query-keys/destinations';
import { hostsApi } from '@/features/hosts/api/lib/hosts';
import { hostsKeys } from '@/features/hosts/api/query-keys/hosts';
import { useHostsWithSearch } from '@/features/hosts/hooks/query/use-hosts-with-search';
import { usePagination } from '@/hooks/use-pagination';

interface Props extends DialogActions {
  destinationId: number;
  hostsIds: number[];
}

const AssignHostsToDestinationDialog = ({
  open,
  onCancel,
  destinationId,
  hostsIds,
}: Props) => {
  const { t } = useTranslation(['destination', 'common']);
  const queryClient = useQueryClient();

  const {
    changePage,
    changePerPage,
    page,
    perPage,
    setTotalItems,
    totalPages,
    nextDisabled,
    nextPage,
    prevDisabled,
    prevPage,
  } = usePagination();

  const [query, setQuery] = useState('');
  const [selectedHostsIds, setSelectedHostsIds] = useState<number[]>(hostsIds || []);

  const { data, isLoading, isFetching, error } = useHostsWithSearch({
    page,
    perPage,
    email: query,
  });

  const handleChangeQuery = (value: string) => {
    setQuery(value);
    changePage(1);
  };

  useEffect(() => {
    if (data) {
      setTotalItems(data.total);
    }
  }, [data, setTotalItems]);

  useEffect(() => {
    if (open) {
      setSelectedHostsIds(hostsIds || []);
    }
  }, [hostsIds, open]);

  const handleToggleHostSelection = (id: number) => {
    setSelectedHostsIds(prev => {
      if (prev.includes(id)) {
        return prev.filter(hostId => hostId !== id);
      }
      return [...prev, id];
    });
  };

  const { hostsIdsToAdd, hostsIdsToRemove } = useMemo(() => {
    return {
      hostsIdsToAdd: selectedHostsIds.filter(id => !hostsIds.includes(id)),
      hostsIdsToRemove: hostsIds.filter(id => !selectedHostsIds.includes(id)),
    };
  }, [hostsIds, selectedHostsIds]);

  const hasChanges = hostsIdsToAdd.length > 0 || hostsIdsToRemove.length > 0;

  const { mutateAsync, isPending } = useMutation({
    mutationFn: async () => {
      await Promise.all([
        ...hostsIdsToAdd.map(hostId =>
          hostsApi.assignSingleDestinationToHost(hostId, destinationId),
        ),
        ...hostsIdsToRemove.map(hostId =>
          hostsApi.unassignSingleDestinationFromHost(hostId, destinationId),
        ),
      ]);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: hostsKeys.all });
      queryClient.invalidateQueries({
        queryKey: destinationsKeys.detail(destinationId),
      });
      toast.success(t('common:popup.success'));
      onCancel?.();
    },
    onError: (apiError: ApiError) => {
      toast.error(apiError?.response?.data?.message);
    },
  });

  return (
    <Dialog
      open={open}
      title={t('destination:hosts.dialogTitle')}
      onCancel={onCancel}
      onConfirm={async () => {
        if (!hasChanges) {
          onCancel?.();
          return;
        }
        await mutateAsync();
      }}
      isConfirmButtonDisabled={!hasChanges || isPending}
      isConfirmButtonSubmitting={isPending}
      variant="info"
      pagination={
        <CompactPagination
          page={page}
          perPage={perPage}
          totalPages={totalPages}
          changePage={changePage}
          disabled={isLoading}
          handlePerPageChange={changePerPage}
          nextDisabled={nextDisabled}
          isFetching={isFetching}
          nextPage={nextPage}
          prevDisabled={prevDisabled}
          prevPage={prevPage}
        />
      }>
      <Box sx={{ mb: 2 }}>
        <Typography variant="body2" sx={{ mb: 1 }}>
          {t('common:search')}
        </Typography>
        <Query
          searchAsType={true}
          disabled={false}
          onChange={newQuery => handleChangeQuery(newQuery)}
          value={query}
        />
      </Box>
      <Divider sx={{ mb: 2 }} />
      <Box sx={{ my: 1 }}>
        {data?.resources?.map(({ id, firstName, lastName, email, phoneNumber }) => {
          const isSelected = selectedHostsIds.includes(id);

          return (
            <ListCard
              key={id}
              name={firstName}
              secondaryName={lastName}
              description={email}
              rightText={phoneNumber}
              isSelected={isSelected}
              onClick={() => handleToggleHostSelection(id)}
            />
          );
        })}
        <DataStatusIndicator
          dataLength={data?.resources?.length ?? 0}
          isFetching={isLoading}
          error={error?.response?.data?.message}
        />
      </Box>
    </Dialog>
  );
};

export default AssignHostsToDestinationDialog;
